import { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import { useCountriesWithZoneList } from './useCountriesStores';
import { useOperatorsNetworksSelect, useOperatorsSelect } from './useOperatorsStores';
import { appConfig } from '../../settings';
import {
    ICheckRate,
    ICountryWithZone,
    INetworkSelectItem,
    IRateCountryFormInfo,
    IRateCountryFormInitialValues,
    IRateInfo,
    IRateListRespEntryV2,
    IRateNetworkItem,
    ISelect,
    ISelectCountries,
    ISelectList,
    ITableFilerItem,
    RareFormTab,
} from '../../interfaces';
import { defaultString, getUniqueTableFilerList, sortByText } from '../../instruments';

export function useRateInfo(): IRateInfo {
    return useContext(MobXProviderContext).RootStore.rateStore.rateInfo;
}

export function useRateDefaultRate(): ICheckRate {
    const rateInfo: IRateInfo = useContext(MobXProviderContext).RootStore.rateStore;
    return {
        rateData: rateInfo.defaultRateData,
        rateDataUnitId: rateInfo.defaultRateDataUnitId,
        rateDataUnit: rateInfo.defaultRateDataUnit,
        rateSms: rateInfo.defaultRateSms,
        rateSmsUnitId: rateInfo.defaultRateSmsUnitId,
        rateSmsUnit: rateInfo.defaultRateSmsUnit,
    };
}

export function useRateList(): IRateCountryFormInfo[] {
    return useContext(MobXProviderContext).RootStore.rateStore.rateCountriesList;
}

export function useRateCountriesList(): IRateCountryFormInitialValues[] {
    return useRateList();
}

export function useRateCustomCountriesCount(): number {
    const defaultRate = useRateDefaultRate();
    return useRateList().filter((i: IRateCountryFormInitialValues) => {
        const { rateData, rateDataUnitId, rateDataUnit, rateSms, rateSmsUnitId, rateSmsUnit } = i;
        const current: ICheckRate = { rateData, rateDataUnitId, rateDataUnit, rateSms, rateSmsUnitId, rateSmsUnit };
        return JSON.stringify(defaultRate) !== JSON.stringify(current);
    }).length;
}

export function useRateNetworksList(): IRateNetworkItem[] {
    return useContext(MobXProviderContext).RootStore.rateStore.rateNetworksList.filter(
        (i: IRateNetworkItem) => !i.destroy,
    );
}

export function useRateCustomNetworksCount(): number {
    const defaultRate = useRateDefaultRate();
    return useRateNetworksList().filter((i: IRateCountryFormInitialValues) => {
        const { rateData, rateDataUnitId, rateDataUnit, rateSms, rateSmsUnitId, rateSmsUnit } = i;
        const current: ICheckRate = { rateData, rateDataUnitId, rateDataUnit, rateSms, rateSmsUnitId, rateSmsUnit };
        return JSON.stringify(defaultRate) !== JSON.stringify(current);
    }).length;
}

export function useRateCountryInitialValuesV2(countryId: string, operatorId: string): IRateCountryFormInitialValues {
    const countries = useRateList();
    const countryInfo = countries.find(
        (r: IRateCountryFormInitialValues) => r.countryId === countryId && r.operatorId === operatorId,
    ) || { id: countryId };
    const networksAll = useRateListV2();
    const networks = networksAll.filter((r) => r.countryId === countryId && r.operatorId === operatorId);
    return {
        ...countryInfo,
        networks: networks as IRateCountryFormInitialValues[],
    };
}
export function useRateCountryInitialValues(countryId: string, operatorId: string): IRateCountryFormInitialValues {
    const countries = useRateList();
    const countryInfo = countries.find(
        (r: IRateCountryFormInitialValues) => r.countryId === countryId && r.operatorId === operatorId,
    ) || { id: countryId };
    const networksAll = useRateNetworksList();
    const networks = networksAll.filter(
        (r: IRateCountryFormInitialValues) => r.countryId === countryId && r.operatorId === operatorId,
    );
    return {
        ...countryInfo,
        networks,
    };
}

export function useRateNetworksListByCountry(countryId: string, operatorId: string): IRateNetworkItem[] {
    const list = useRateNetworksList();
    return list.filter((r: IRateCountryFormInitialValues) => r.countryId === countryId && r.operatorId === operatorId);
}

export function useCountryListIsLoading(): boolean {
    const alreadyLoaded = useContext(MobXProviderContext).RootStore.rateStore.countryLoadingStats.alreadyLoaded;
    const expectedToLoad = useContext(MobXProviderContext).RootStore.rateStore.countryLoadingStats.expectedToLoad;

    return alreadyLoaded !== expectedToLoad;
}

export function useIsCountryListLoadingFinished(): boolean {
    const alreadyLoaded = useContext(MobXProviderContext).RootStore.rateStore.countryLoadingStats.alreadyLoaded;
    const expectedToLoad = useContext(MobXProviderContext).RootStore.rateStore.countryLoadingStats.expectedToLoad;

    return alreadyLoaded === expectedToLoad;
}
export function useRatesIsFakeOperator(): boolean {
    return useContext(MobXProviderContext).RootStore.rateStore.isFakeOperatorRateCountriesListOriginal;
}

export function useRateViewListOperatorsSelect(): ISelectList {
    const isFakeOperator = useRatesIsFakeOperator();
    const operatorsSelect = useOperatorsSelect();

    return operatorsSelect.filter((i: ISelect) => isFakeOperator || i.id !== appConfig.fakeId);
}
export function useRateChangedFormTabs(): Array<RareFormTab> {
    return useContext(MobXProviderContext).RootStore.rateStore.changedFormTabs;
}
export function useRateListV2(): Array<IRateListRespEntryV2> {
    return useContext(MobXProviderContext).RootStore.rateStore.rateListV2;
}

export function useRateListOperatorFilterList(): Array<ITableFilerItem> {
    const list = useRateListV2().map(
        (i: IRateListRespEntryV2) =>
            ({
                value: i.operatorId || defaultString,
                text: i.operatorId ? i.operatorName || i.operatorId : 'Default',
            } as ITableFilerItem),
    );
    const uniqueList = getUniqueTableFilerList(list);
    return uniqueList.sort(sortByText);
}

export function useRateListZoneFilterList(): Array<ITableFilerItem> {
    const list = useRateListV2()
        .filter((i: IRateListRespEntryV2) => i.zoneId)
        .map(
            (i: IRateListRespEntryV2) =>
                ({
                    value: i.zoneId,
                    text: i.zoneName || i.zoneId,
                } as ITableFilerItem),
        );
    const uniqueList = getUniqueTableFilerList(list);
    return uniqueList.sort(sortByText);
}
export function useRateListCountryFilterList(): Array<ITableFilerItem> {
    const list = useRateListV2()
        .filter((i: IRateListRespEntryV2) => i.countryId)
        .map(
            (i: IRateListRespEntryV2) =>
                ({
                    value: i.countryId,
                    text: i.countryName || i.countryId,
                } as ITableFilerItem),
        );
    const uniqueList = getUniqueTableFilerList(list);
    return uniqueList.sort(sortByText);
}

export function useRateListNetworkFilterList(): Array<ITableFilerItem> {
    const list = useRateListV2()
        .filter((i: IRateListRespEntryV2) => i.networkId)
        .map(
            (i: IRateListRespEntryV2) =>
                ({
                    value: i.networkId,
                    text: i.networkName || i.networkId,
                } as ITableFilerItem),
        );
    const uniqueList = getUniqueTableFilerList(list);
    return uniqueList.sort(sortByText);
}

export const useRateEditCountriesByZoneSelectV2 = (): Array<ISelectCountries> => {
    const zoneId = useContext(MobXProviderContext).RootStore.rateStore.editSelectedZoneId;
    const countriesList = useCountriesWithZoneList();

    return (zoneId ? countriesList.filter((c: ICountryWithZone): boolean => c.zoneId === zoneId) : countriesList).map(
        (i: ICountryWithZone) => ({
            ...i,
            name: i.name || i.id,
        }),
    );
};
export const useRateEditNetworksByCountryZoneSelectV2 = (): Array<INetworkSelectItem> => {
    const countryId = useContext(MobXProviderContext).RootStore.rateStore.editSelectedCountryId;
    const networksList = useOperatorsNetworksSelect();
    const countries = useRateEditCountriesByZoneSelectV2();

    if (countryId) {
        return networksList.filter((n: INetworkSelectItem): boolean => n.countryId === countryId);
    }

    if (countries.length) {
        const countriesIds = countries.map((i) => i.id);
        return networksList.filter(
            (n: INetworkSelectItem): boolean => !!n.countryId && countriesIds.includes(n.countryId),
        );
    }

    return networksList;
};
